/**
 * SRES · Cockpit data-fetch hook (Iter98k · Feb 2026 refactor)
 *
 * Behaviour-preserving extraction of the six React-Query calls that
 * previously lived inline inside CockpitPage.jsx. Query keys, options
 * (`enabled`, `staleTime`, `refetchInterval`) and error-handling are
 * unchanged so React-Query's cache continues to hit the exact same
 * entries the rest of the app already writes to.
 */
import { useQuery } from "@tanstack/react-query";
import { api, getBlockModel, getEngineeringPit,
         getGeoModelSummary, getGeoAssayDiscs } from "@/lib/api";


export function useCockpitQueries(pid, opts = {}) {
  const { showMineDesignPit = false, showAssayDiscs = false } = opts;

  const projectQ = useQuery({
    queryKey: ["cockpit-project", pid],
    queryFn: () => api.get(`/projects/${pid}`).then((r) => r.data),
    enabled: !!pid,
  });
  const bmQ = useQuery({
    queryKey: ["cockpit-bm", pid],
    queryFn: () => getBlockModel(pid),
    enabled: !!pid,
  });
  const holesQ = useQuery({
    queryKey: ["cockpit-holes", pid],
    queryFn: () => api.get(`/projects/${pid}/drillholes3d`).then((r) => r.data),
    enabled: !!pid,
  });

  const mineSummaryQ = useQuery({
    queryKey: ["cockpit-mine-summary", pid],
    queryFn:  () => api.get(`/projects/${pid}/mine-design/summary`).then((r) => r.data)
                        .catch(() => ({})),
    enabled:  !!pid,
    staleTime: 30_000,
  });
  const pitDesignQ = useQuery({
    queryKey: ["cockpit-mine-pit", pid],
    queryFn:  () => getEngineeringPit(pid).catch(() => null),
    enabled:  !!pid && showMineDesignPit,
  });

  const geoSummaryQ = useQuery({
    queryKey: ["cockpit-geo-summary", pid],
    queryFn:  () => getGeoModelSummary(pid),
    enabled:  !!pid,
    refetchInterval: 30_000,
  });
  const assayDiscsQ = useQuery({
    queryKey: ["cockpit-assay-discs", pid],
    queryFn:  () => getGeoAssayDiscs(pid),
    enabled:  !!pid && showAssayDiscs,
  });

  return {
    projectQ, bmQ, holesQ,
    mineSummaryQ, pitDesignQ,
    geoSummaryQ, assayDiscsQ,
  };
}
